import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IJobsApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { formatJobDate, formatJobMoney } from '../utils/jobPresentation';
import { jobStatusLabel } from '../pages/jobUi';
import { inputClass, labelClass, secondaryButtonClass } from './formStyles';
import { Toast } from './Toast';
import { EnrollmentChip, JobEnrollmentChip } from '../features/service-bundles/EnrollmentChip';
import { groupJobsByEnrollment } from '../features/service-bundles/bundleUi';

const PAGE_SIZE = 10;
const STATUSES = ['', 'DRAFT', 'WORK_ORDER_GENERATED', 'SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'INVOICED', 'CANCELLED'];

type CustomerJobsSectionProps = (
  /** Every job for the customer (all of its properties) */
  | { customerId: string; propertyId?: undefined }
  /** Only the jobs at one property (property detail page); the Property column is hidden */
  | { propertyId: string; customerId?: string }
) & {
  /** The viewer may open enrollment pages (office roles); the bundle chip is a plain label otherwise */
  canOpenEnrollments?: boolean;
};

/**
 * Every job performed for this customer (all properties, all statuses), or
 * at one property when `propertyId` is given, with status and scheduled-date
 * filters. Jobs belong to the customer who owns the property; bill-to
 * arrangements live on invoices, so they are not listed here.
 */
export function CustomerJobsSection({ customerId, propertyId, canOpenEnrollments = false }: CustomerJobsSectionProps) {
  const scope = propertyId ? 'property-jobs' : 'customer-jobs';
  const headings = propertyId ? ['Scheduled', 'Status', 'Total'] : ['Scheduled', 'Property', 'Status', 'Total'];
  const api = useMemo<IJobsApi>(() => createAPIClient(), []);
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [jobs, setJobs] = useState<types.Job[] | null>(null);
  const [pages, setPages] = useState(1);
  /** Group the page's jobs by bundle enrollment (tracker item 85) */
  const [grouped, setGrouped] = useState(false);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // A filter change goes back to page 1
  useEffect(() => { setPage(1); }, [status, from, to, customerId, propertyId]);

  useEffect(() => {
    let active = true;
    setJobs(null);
    setError(null);
    api.listJobs(page, PAGE_SIZE, {
      customerId: propertyId ? undefined : customerId,
      propertyId,
      status: status ? status as types.JobStatus : undefined,
      scheduledFrom: from || undefined,
      scheduledTo: to || undefined,
    })
      .then((result) => {
        if (!active) return;
        setJobs(result.data);
        setPages(result.pagination.pages);
        setTotal(result.pagination.total);
      })
      .catch((err) => { if (active) setError(getErrorMessage(err, 'Failed to load jobs')); });
    return () => { active = false; };
  }, [api, customerId, propertyId, page, status, from, to]);

  const filtered = Boolean(status || from || to);
  const hasBundles = Boolean(jobs?.some((job) => job.enrollmentId));

  return (
    <section aria-labelledby={`${scope}-heading`} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
      <h2 id={`${scope}-heading`} className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
        Job history {jobs && <span className="text-gray-400 font-normal">({total})</span>}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div>
          <label htmlFor={`${scope}-status`} className={labelClass}>Status</label>
          <select id={`${scope}-status`} className={inputClass} value={status} onChange={(event) => setStatus(event.target.value)}>
            {STATUSES.map((option) => <option key={option} value={option}>{option ? jobStatusLabel(option) : 'All statuses'}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor={`${scope}-from`} className={labelClass}>Scheduled from</label>
          <input id={`${scope}-from`} type="date" className={inputClass} value={from} max={to || undefined} onChange={(event) => setFrom(event.target.value)} />
        </div>
        <div>
          <label htmlFor={`${scope}-to`} className={labelClass}>Scheduled to</label>
          <input id={`${scope}-to`} type="date" className={inputClass} value={to} min={from || undefined} onChange={(event) => setTo(event.target.value)} />
        </div>
      </div>
      {hasBundles && (
        <label className="mb-3 inline-flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" className="h-4 w-4" checked={grouped} onChange={(event) => setGrouped(event.target.checked)} />
          Group by enrollment
        </label>
      )}
      {error ? (
        <><Toast kind="error">{error}</Toast><p className="text-gray-500 text-sm">Jobs could not be loaded.</p></>
      ) : jobs === null ? (
        <p role="status" className="text-gray-500 text-sm">Loading jobs...</p>
      ) : jobs.length === 0 ? (
        <p className="text-gray-500 text-sm">{filtered ? 'No jobs match these filters.' : (propertyId ? 'No jobs at this property yet.' : 'No jobs for this customer yet.')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="responsive-table min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {headings.map((heading) => (
                  <th key={heading} scope="col" className="px-4 py-3 text-left text-gray-600 font-medium">{heading}</th>
                ))}
              </tr>
            </thead>
            {(grouped && hasBundles ? groupJobsByEnrollment(jobs) : [{ key: 'all', ref: null, jobs }]).map((group) => (
              <tbody key={group.key} className="divide-y divide-gray-200 border-t border-gray-200">
                {grouped && hasBundles && (
                  <tr className="bg-gray-50" data-testid="job-group">
                    <th scope="colgroup" colSpan={headings.length} className="px-4 py-2 text-left font-medium text-gray-700">
                      {group.ref ? <span className="inline-flex flex-wrap items-center gap-2"><EnrollmentChip {...group.ref} linked={canOpenEnrollments} /><span className="text-xs font-normal text-gray-600">{group.jobs.length} of {group.ref.enrollmentStepCount} visits on this page</span></span> : 'Not part of a bundle'}
                    </th>
                  </tr>
                )}
                {group.jobs.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td data-label="Scheduled" data-primary className="px-4 py-3 font-semibold">
                      <Link to={`/jobs/${job.id}`} className="text-blue-700 hover:underline">{formatJobDate(job.scheduledDate)}</Link>
                    </td>
                    {!propertyId && <td data-label="Property" className="px-4 py-3 break-words">{job.property?.address || job.propertyId}</td>}
                    <td data-label="Status" className="px-4 py-3"><span className="flex flex-wrap items-center gap-2">{jobStatusLabel(job.status)}{!(grouped && hasBundles) && <JobEnrollmentChip job={job} linked={canOpenEnrollments} />}</span></td>
                    <td data-label="Total" className="px-4 py-3">{formatJobMoney(job.total)}</td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}
      {pages > 1 && !error && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <button type="button" className={`${secondaryButtonClass} min-h-[44px] sm:min-h-0`} disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span className="text-sm text-gray-600">Page {page} of {pages}</span>
          <button type="button" className={`${secondaryButtonClass} min-h-[44px] sm:min-h-0`} disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </section>
  );
}
